/**
 * 정변위 펌프/압축기 최대유입, 배관 최대유입, 밸브(제어밸브·체크밸브) 고장
 * 시 소요 분출용량 산정.
 *
 * ⚠ KOSHA GUIDE D-18-2020 <표 2> 와 API 521 4.4 는 이 케이스들에 대해
 *   "제어밸브 위치·개수·고장모드에 따라 사례별로 계산"이라고만 하고
 *   닫힌 계산식을 주지 않는다(§5.1·§5.6·§5.7·§5.8, API 521 4.4.2/4.4.7/
 *   4.4.8/4.4.9). 이 모듈의 식은 표준이 지정한 것이 아니라 **일반
 *   유체역학(질량보존·베르누이/오리피스 유동식)을 그대로 적용한 것**이다.
 *   계산서에는 "API 521 식 n" 이 아니라 이 사실을 명시해야 한다.
 *
 * 단위: 압력 kPa(절대) · 온도 K · 밀도 kg/m³ · 지름 mm · 유량 kg/h
 */

const R_UNIVERSAL = 8314.462618; // J/(kmol·K)

function invalid(message: string): { ok: false; reason: "invalid-input"; message: string } {
  return { ok: false, reason: "invalid-input", message };
}

function checkPositive(label: string, v: number) {
  return !Number.isFinite(v) || v <= 0
    ? `${label} 은(는) 0보다 큰 유한한 값이어야 합니다.`
    : null;
}

/* ── 정변위 펌프 / 정변위 압축기 최대유입 ──────────────────────
 * KOSHA D-18-2020 4.1(1)(다) "정변위 펌프(토출측 막힘으로 인한 압력상승이
 * 관련기기의 설계압력을 구조적으로 초과할 수 있도록 제작된 펌프류)".
 * 정변위 기계는 배압과 무관하게 정격 토출량을 그대로 밀어내므로, 소요
 * 분출용량은 정격 토출량 자체다(질량유량 = 체적유량 × 밀도). */

export interface PdPumpLiquidInput {
  readonly fluidState: "liquid";
  /** 정격 토출량 (m³/h) */
  readonly ratedVolumeFlowM3H: number;
  /** 배출 조건 밀도 (kg/m³) */
  readonly densityKgM3: number;
  /**
   * 사용자 정의 계수 (기본 1.0). **표준 근거가 없다.**
   * 우회(bypass) 라인 등으로 실제 필요 용량이 줄어든다는 근거가 있을
   * 때만 1 미만으로 낮추고, 그 근거를 계산서에 반드시 남길 것.
   */
  readonly capacityFactor?: number;
}

export interface PdPumpGasInput {
  readonly fluidState: "gas";
  /** 정격 토출량 — 흡입 조건(실제) 기준 (m³/h) */
  readonly ratedVolumeFlowM3H: number;
  readonly suctionPressureKpaAbs: number;
  readonly suctionTemperatureK: number;
  readonly molecularWeight: number;
  readonly compressibility?: number;
  readonly capacityFactor?: number;
}

export type PdPumpInput = PdPumpLiquidInput | PdPumpGasInput;

export interface PdPumpSuccess {
  readonly ok: true;
  readonly massFlowKgH: number;
  readonly densityKgM3: number;
  readonly warnings: readonly string[];
}
export interface PdPumpFailure {
  readonly ok: false;
  readonly reason: "invalid-input";
  readonly message: string;
}
export type PdPumpResult = PdPumpSuccess | PdPumpFailure;

export function positiveDisplacementRelievingRate(
  input: PdPumpInput,
): PdPumpResult {
  const factor = input.capacityFactor ?? 1.0;
  if (!Number.isFinite(factor) || factor <= 0 || factor > 1) {
    return invalid("계수는 0보다 크고 1 이하이어야 합니다.");
  }
  const rateErr = checkPositive("정격 토출량", input.ratedVolumeFlowM3H);
  if (rateErr) return invalid(rateErr);

  let densityKgM3: number;
  if (input.fluidState === "liquid") {
    const err = checkPositive("밀도", input.densityKgM3);
    if (err) return invalid(err);
    densityKgM3 = input.densityKgM3;
  } else {
    for (const [label, v] of [
      ["흡입측 압력", input.suctionPressureKpaAbs],
      ["흡입측 온도", input.suctionTemperatureK],
      ["분자량", input.molecularWeight],
    ] as const) {
      const err = checkPositive(label, v);
      if (err) return invalid(err);
    }
    const Z = input.compressibility ?? 1.0;
    if (!Number.isFinite(Z) || Z <= 0) return invalid("압축계수 Z 는 0보다 커야 합니다.");
    densityKgM3 =
      (input.suctionPressureKpaAbs * 1000 * input.molecularWeight) /
      (Z * R_UNIVERSAL * input.suctionTemperatureK);
  }

  const massFlowKgH = input.ratedVolumeFlowM3H * densityKgM3 * factor;

  const warnings: string[] = [
    "정변위 기계는 배압과 무관하게 정격 토출량을 그대로 밀어냅니다 — 소요 " +
      "분출용량은 정격 토출량 자체입니다(KOSHA GUIDE D-18-2020 4.1(1)(다)).",
  ];
  if (factor < 1) {
    warnings.push(
      `계수 ${factor} 를 곱해 소요량을 낮췄습니다. **이것은 표준 근거가 아닌 ` +
        "사용자 지정값입니다.** 우회배관 등으로 실제 필요 용량이 줄어드는 " +
        "근거를 계산서·PSM 에 반드시 남기십시오. 이미 선정된 밸브 규격에 " +
        "맞추기 위해 소요량 쪽을 낮추는 것은 설계 순서가 거꾸로 된 것이니 " +
        "특히 주의하십시오.",
    );
  }

  return { ok: true, massFlowKgH, densityKgM3, warnings };
}

/* ── 배관 최대유입 (표준유속 기준) ──────────────────────────
 * KOSHA §5.1·§5.6 이 "최대 유입량"이라고만 하고 구하는 방법은 정하지
 * 않는다. 배관을 유체별 설계(표준) 유속으로 가득 채워 흐른다고 보는
 * 관행적 산정법 — **표준식이 아니라 사내 설계기준**이다. */

export interface PipingVelocityInflowInput {
  readonly innerDiameterMm: number;
  /** 사내 설계기준 유속 (m/s) */
  readonly velocityMs: number;
  readonly densityKgM3: number;
}

export interface PipingVelocityInflowSuccess {
  readonly ok: true;
  readonly massFlowKgH: number;
  readonly volumeFlowM3H: number;
  readonly warnings: readonly string[];
}
export type PipingVelocityInflowResult =
  | PipingVelocityInflowSuccess
  | PdPumpFailure;

export function pipingVelocityInflowRate(
  input: PipingVelocityInflowInput,
): PipingVelocityInflowResult {
  for (const [label, v] of [
    ["관 내경", input.innerDiameterMm],
    ["유속", input.velocityMs],
    ["밀도", input.densityKgM3],
  ] as const) {
    const err = checkPositive(label, v);
    if (err) return invalid(err);
  }

  const dM = input.innerDiameterMm / 1000;
  const areaM2 = (Math.PI / 4) * dM * dM;
  const volumeFlowM3H = areaM2 * input.velocityMs * 3600;
  const massFlowKgH = volumeFlowM3H * input.densityKgM3;

  return {
    ok: true,
    massFlowKgH,
    volumeFlowM3H,
    warnings: [
      "이 유속은 KOSHA·API 표준이 정한 값이 아니라 **사내 설계기준(관행) " +
        "유속**입니다. 계산서에 유속의 출처(사내 기준 문서 등)를 남기십시오.",
    ],
  };
}

/* ── 제어밸브·체크밸브 고장 (압력차 기반 오리피스 유동) ─────────
 * KOSHA §5.7·§5.8, API 521 4.4.8/4.4.9 — "제어밸브 위치·개수·
 * 고장모드에 따라 사례별로 계산"만 규정하고 식은 없다. 고장난 밸브를
 * 배관 단면적 크기의 오리피스로 보고, 압력차에서 유속을 구하는(베르누이/
 * 토리첼리) 방식 — API 521 4.4.14.2.2 튜브 파열 케이스와 물리적으로
 * 같은 오리피스 유동식이며, 개구부는 1개(밸브 1개)로 둔다. */

export interface ValveFailureLiquidInput {
  readonly innerDiameterMm: number;
  /** 감압 전(상류) 압력 (kPa 절대) */
  readonly upstreamPressureKpaAbs: number;
  /** 감압 후(하류) 압력 (kPa 절대) */
  readonly downstreamPressureKpaAbs: number;
  readonly densityKgM3: number;
  /** 배출계수. 기본 1.0(보수적) — 표준이 값을 정하지 않는다. */
  readonly dischargeCoefficient?: number;
}

export interface ValveFailureLiquidSuccess {
  readonly ok: true;
  readonly massFlowKgH: number;
  readonly velocityMs: number;
  readonly warnings: readonly string[];
}
export type ValveFailureLiquidResult =
  | ValveFailureLiquidSuccess
  | PdPumpFailure;

export function valveFailureLiquidRate(
  input: ValveFailureLiquidInput,
): ValveFailureLiquidResult {
  for (const [label, v] of [
    ["관 내경", input.innerDiameterMm],
    ["밀도", input.densityKgM3],
  ] as const) {
    const err = checkPositive(label, v);
    if (err) return invalid(err);
  }
  const Cd = input.dischargeCoefficient ?? 1.0;
  if (!Number.isFinite(Cd) || Cd <= 0 || Cd > 1) {
    return invalid("배출계수는 0보다 크고 1 이하이어야 합니다.");
  }
  const { upstreamPressureKpaAbs: p1, downstreamPressureKpaAbs: p2 } = input;
  if (!Number.isFinite(p1) || !Number.isFinite(p2)) {
    return invalid("압력이 올바르지 않습니다.");
  }
  if (p1 <= p2) {
    return invalid(
      "상류(감압 전) 압력이 하류(감압 후) 압력보다 커야 합니다. 두 값이 " +
        "절대압인지 확인하십시오.",
    );
  }

  const dM = input.innerDiameterMm / 1000;
  const areaM2 = (Math.PI / 4) * dM * dM;
  const dpPa = (p1 - p2) * 1000;
  // v = √(2·ΔP/ρ)  (토리첼리 — h = ΔP/(ρg) 를 v = √(2gh) 에 대입한 것과 같다)
  const velocityMs = Math.sqrt((2 * dpPa) / input.densityKgM3);
  const massFlowKgS = Cd * areaM2 * velocityMs * input.densityKgM3;

  return {
    ok: true,
    massFlowKgH: massFlowKgS * 3600,
    velocityMs,
    warnings: [
      "밸브 고장 지점을 배관 단면적 크기의 오리피스로 보고, 압력차에서 " +
        "유속(베르누이/토리첼리 v=√(2ΔP/ρ))을 구한 값입니다. KOSHA·API " +
        "표준이 지정한 식이 아니라 일반 유체역학을 적용한 것입니다.",
      `배출계수 ${Cd} 를 적용했습니다. 표준이 값을 정하지 않으므로 근거를 ` +
        "남기십시오.",
    ],
  };
}

export interface ValveFailureGasInput {
  readonly innerDiameterMm: number;
  readonly upstreamPressureKpaAbs: number;
  readonly downstreamPressureKpaAbs: number;
  readonly temperatureK: number;
  readonly molecularWeight: number;
  readonly specificHeatRatio: number;
  readonly compressibility?: number;
  readonly dischargeCoefficient?: number;
}

export interface ValveFailureGasSuccess {
  readonly ok: true;
  readonly massFlowKgH: number;
  readonly critical: boolean;
  readonly warnings: readonly string[];
}
export type ValveFailureGasResult = ValveFailureGasSuccess | PdPumpFailure;

/** 임계(초크) 압력비 — (2/(k+1))^(k/(k−1)) */
function criticalRatio(k: number): number {
  return Math.pow(2 / (k + 1), k / (k - 1));
}

export function valveFailureGasRate(
  input: ValveFailureGasInput,
): ValveFailureGasResult {
  for (const [label, v] of [
    ["관 내경", input.innerDiameterMm],
    ["온도", input.temperatureK],
    ["분자량", input.molecularWeight],
  ] as const) {
    const err = checkPositive(label, v);
    if (err) return invalid(err);
  }
  const { specificHeatRatio: k } = input;
  if (!Number.isFinite(k) || k <= 1) return invalid("비열비 k 는 1보다 커야 합니다.");
  const Z = input.compressibility ?? 1.0;
  if (!Number.isFinite(Z) || Z <= 0) return invalid("압축계수 Z 는 0보다 커야 합니다.");
  const Cd = input.dischargeCoefficient ?? 1.0;
  if (!Number.isFinite(Cd) || Cd <= 0 || Cd > 1) {
    return invalid("배출계수는 0보다 크고 1 이하이어야 합니다.");
  }
  const { upstreamPressureKpaAbs: p1, downstreamPressureKpaAbs: p2 } = input;
  if (!Number.isFinite(p1) || !Number.isFinite(p2) || p2 < 0) {
    return invalid("압력이 올바르지 않습니다.");
  }
  if (p1 <= p2) {
    return invalid(
      "상류(감압 전) 압력이 하류(감압 후) 압력보다 커야 합니다. 두 값이 " +
        "절대압인지 확인하십시오.",
    );
  }

  const dM = input.innerDiameterMm / 1000;
  const areaM2 = (Math.PI / 4) * dM * dM;
  const p1Pa = p1 * 1000;
  const r = p2 / p1;
  const rc = criticalRatio(k);
  const critical = r <= rc;

  const base = input.molecularWeight / (Z * R_UNIVERSAL * input.temperatureK);
  const term = critical
    ? k * base * Math.pow(2 / (k + 1), (k + 1) / (k - 1))
    : 2 * base * (k / (k - 1)) * (Math.pow(r, 2 / k) - Math.pow(r, (k + 1) / k));

  const massFlowKgS = Cd * areaM2 * p1Pa * Math.sqrt(Math.max(term, 0));

  return {
    ok: true,
    massFlowKgH: massFlowKgS * 3600,
    critical,
    warnings: [
      "밸브 고장 지점을 배관 단면적 크기의 오리피스로 보고 압축성 유동식을 " +
        "적용한 값입니다. KOSHA·API 표준이 지정한 식이 아니라 일반 " +
        "유체역학(오리피스 유동)을 적용한 것입니다.",
      `배출계수 ${Cd}, 압축계수 ${Z} 를 적용했습니다. 표준이 값을 정하지 ` +
        "않으므로 근거를 남기십시오.",
      critical
        ? "임계(초크) 유동입니다 — 하류 압력이 변해도 유량은 늘지 않습니다."
        : "아임계 유동입니다 — 하류 압력에 유량이 민감하므로 조건을 확인하십시오.",
    ],
  };
}
